import type { IJSONSchema } from '../../types';
import { searchJmesPath } from '../../utils';

const REFERENCE_VALUE_KEYS = new Set([
  'id',
  'modelId',
  'version',
  '_formDisplayValue',
  '_meta',
  '_formLocal',
]);

const isSourceObject = (obj: any): boolean =>
  Object.keys(obj).some(k => !REFERENCE_VALUE_KEYS.has(k));

/**
 * Recursively searches the data tree for an object with the given id.
 * Only returns objects that have domain fields beyond standard IReferenceValue keys,
 * so sparse stored references { id } are never matched over real source objects.
 */
export const findByIdInData = (data: any, id: string | undefined): any => {
  if (!data || !id) return null;

  if (Array.isArray(data)) {
    for (const item of data) {
      const found = findByIdInData(item, id);
      if (found) return found;
    }
    return null;
  }

  if (typeof data === 'object') {
    if (data.id === id && isSourceObject(data)) return data;
    for (const value of Object.values(data)) {
      if (value && typeof value === 'object') {
        const found = findByIdInData(value, id);
        if (found) return found;
      }
    }
  }

  return null;
};

const isSparseIdRef = (value: any): boolean =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length === 1 &&
  'id' in value;

/**
 * Recursively walks data (no schema needed) and replaces any object that only
 * has an `id` key (e.g. `{ id: "123" }`) with the full matching object found
 * elsewhere in the data tree via findByIdInData. If no match is found, the
 * sparse `{ id }` is left untouched.
 */
export const enrichSparseIdRefs = (data: any, rootData: any = data): any => {
  if (isSparseIdRef(data)) {
    const found = findByIdInData(rootData, data.id);
    return found ?? data;
  }

  if (Array.isArray(data)) {
    return data.map(item => enrichSparseIdRefs(item, rootData));
  }

  if (data && typeof data === 'object') {
    const result: any = {};
    for (const [key, value] of Object.entries(data)) {
      result[key] = enrichSparseIdRefs(value, rootData);
    }
    return result;
  }

  return data;
};

const resolveReference = (
  stored: any,
  sourcePath: string,
  rootData: any
): any => {
  if (!stored || typeof stored !== 'object' || !stored.id) return stored;

  let sourceArray: any;
  try {
    sourceArray = searchJmesPath(rootData, sourcePath);
  } catch {
    return stored;
  }

  if (!Array.isArray(sourceArray)) return stored;

  const match = sourceArray.find((item: any) => item?.id === stored.id);
  return match ?? stored;
};

const walkNode = (data: any, schema: IJSONSchema, rootData: any): any => {
  if (data === null || data === undefined) return data;

  if (
    schema['x-type'] === 'internal-reference' &&
    schema['x-internal-reference']?.path
  ) {
    return resolveReference(
      data,
      schema['x-internal-reference'].path,
      rootData
    );
  }

  if (schema.type === 'object' && schema.properties) {
    const result = { ...data };
    for (const [key, propSchema] of Object.entries(schema.properties)) {
      if (data[key] !== undefined && data[key] !== null) {
        result[key] = walkNode(data[key], propSchema as IJSONSchema, rootData);
      }
    }
    return result;
  }

  if (schema.type === 'array' && schema.items && Array.isArray(data)) {
    const itemSchema = Array.isArray(schema.items)
      ? schema.items[0]
      : schema.items;
    if (!itemSchema) return data;
    return data.map((item: any) =>
      walkNode(item, itemSchema as IJSONSchema, rootData)
    );
  }

  return data;
};

/**
 * Walks the JSON schema alongside BE data.
 * For any field marked x-type: "internal-reference" with x-internal-reference.path,
 * finds the matching item in the source array (resolved from root data via path)
 * and replaces the sparse { id } stored value with the full matched object.
 *
 * Example: attachments[].referenceTo[] has path "@.sudCases[]"
 * Stored: { id: "123" } → replaced with the full sudCase object { id: "123", caseNumber: "...", ... }
 */
export const enrichInternalReferenceData = (
  data: any,
  schema: IJSONSchema
): any => {
  if (!data || !schema) return data;
  return walkNode(data, schema, data);
};
